// src/pages/EditVessel.js
import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { api } from "../api/client";

// AIS type codes that warrant showing equipment fields
const WORKING_AIS_TYPES = new Set([31, 32, 33, 34, 35, 50, 52, 53, 54, 58]);

// Oskar vessel type IDs that warrant showing equipment fields (all of them)
const WORKING_OSKAR_TYPES = new Set([51, 53, 57, 59, 62, 63, 64, 65, 66, 67, 68]);

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

function ReadOnlyField({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <div style={{ fontSize: 11, color: "#6b8cae", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: "#1e3a5f", background: "#f8fafc", border: "1px solid #d0dcea", borderRadius: 6, padding: "8px 12px" }}>
        {value}
      </div>
    </div>
  );
}

export default function EditVessel() {
  const { id } = useParams();
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;
  const cols2 = isMobile ? "1fr" : "1fr 1fr";
  const cols3 = isMobile ? "1fr" : windowWidth < 900 ? "1fr 1fr" : "1fr 1fr 1fr";

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showExtras, setShowExtras] = useState(false);

  // Read-only AIS + Equasis data
  const [vesselData, setVesselData] = useState(null);

  // Editable fields only
  const [form, setForm] = useState({
    certification: "",
    extra_type_ids: [],
    mbes: "",
    sss: "",
    mag: "",
    a_frame: "",
    accommodation_guests: "",
    helideck: "",
    moonpool: "",
    fifi_pump: "",
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [v, t] = await Promise.all([
          api(`/vessels/${id}?user_id=${user.id}`, { token }),
          api(`/vessel-types`, { token }),
        ]);
        if (!alive) return;
        setTypes(Array.isArray(t) ? t : []);
        setVesselData(v);

        const yn = (b) => (b === true ? "Yes" : b === false ? "No" : "");
        setForm({
          certification: v.certification || "",
          extra_type_ids: Array.isArray(v.extra_type_ids) ? v.extra_type_ids : [],
          mbes: yn(v.mbes),
          sss: yn(v.sss),
          mag: yn(v.mag),
          a_frame: yn(v.a_frame),
          accommodation_guests: yn(v.accommodation_guests),
          helideck: yn(v.helideck),
          moonpool: yn(v.moonpool),
          fifi_pump: yn(v.fifi_pump),
        });
      } catch {
        setErr("Failed to load vessel.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, token, user.id]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onChangeExtras = (tid) => {
    setForm((p) => {
      const set = new Set(p.extra_type_ids || []);
      if (set.has(tid)) set.delete(tid);
      else set.add(tid);
      return { ...p, extra_type_ids: Array.from(set) };
    });
  };

  const sendYesNo = (v) => (v === "" ? null : v);

  const onSave = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api(`/vessels/${id}`, {
        method: "PUT",
        token,
        body: {
          user_id: user.id,
          // Pass through read-only fields unchanged
          name: vesselData.name || null,
          mmsi: vesselData.mmsi || null,
          imo: vesselData.imo || null,
          callsign: vesselData.callsign || null,
          length: vesselData.length ?? null,
          width: vesselData.width ?? null,
          draft: vesselData.draft ?? null,
          company_id: vesselData.company_id ?? null,
          // Editable fields
          certification: form.certification || null,
          extra_type_ids: form.extra_type_ids,
          mbes: sendYesNo(form.mbes),
          sss: sendYesNo(form.sss),
          mag: sendYesNo(form.mag),
          a_frame: sendYesNo(form.a_frame),
          accommodation_guests: sendYesNo(form.accommodation_guests),
          helideck: sendYesNo(form.helideck),
          moonpool: sendYesNo(form.moonpool),
          fifi_pump: sendYesNo(form.fifi_pump),
        },
      });
      navigate(-1);
    } catch {
      setErr("Save failed.");
    }
  };

  const inputStyle = {
    border: "1px solid #d0dcea", borderRadius: 6, padding: "8px 10px",
    fontSize: 14, width: "100%", boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block", fontSize: 13, fontWeight: 500, color: "#3a5068", marginBottom: 4,
  };

  const YesNoSelect = ({ name, label }) => (
    <div>
      <label style={labelStyle}>{label}</label>
      <select
        name={name}
        value={form[name]}
        onChange={onChange}
        style={inputStyle}
      >
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </div>
  );

  const sectionHeader = {
    fontSize: 11, fontWeight: 600, color: "#6b8cae",
    textTransform: "uppercase", letterSpacing: "0.08em",
  };

  if (loading) return <div style={{ paddingTop: 64, padding: 24, fontSize: 14, color: "#6b8cae" }}>Loading…</div>;
  if (!vesselData) return <div style={{ paddingTop: 64, padding: 24, fontSize: 14, color: "#c0392b" }}>{err || "Vessel not found."}</div>;

  // Determine if equipment fields should show
  const aisType = vesselData.ais_type ?? vesselData.type ?? null;
  const hasWorkingAisType = aisType !== null && WORKING_AIS_TYPES.has(Number(aisType));
  const hasWorkingOskarType =
    (vesselData.type_id && WORKING_OSKAR_TYPES.has(Number(vesselData.type_id))) ||
    (form.extra_type_ids || []).some(tid => WORKING_OSKAR_TYPES.has(Number(tid)));
  const showEquipment = hasWorkingAisType || hasWorkingOskarType;

  // Equasis fields present?
  const hasEquasis = vesselData.equasis_owner || vesselData.equasis_manager ||
    vesselData.equasis_flag || vesselData.equasis_gt;

  const companyName = vesselData.equasis_manager || vesselData.equasis_owner || null;

  return (
    <div style={{ paddingTop: 64, minHeight: "100vh", background: "#e2eaf4", paddingLeft: 16, paddingRight: 16, paddingBottom: 24 }}>
      <div style={{ maxWidth: 768, margin: "0 auto", display: "flex", flexDirection: "column", gap: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e3a5f", margin: 0 }}>Edit Vessel</h1>
          <button
            type="button"
            onClick={() => navigate("/vessels")}
            style={{ fontSize: 13, color: "#6b8cae", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            ← Back to vessels
          </button>
        </div>
        {err && <div style={{ fontSize: 14, color: "#c0392b" }}>{err}</div>}

        {/* ── Read-only: AIS data ── */}
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #d0dcea", padding: "24px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={sectionHeader}>
            Vessel identity
          </div>
          <div style={{ display: "grid", gridTemplateColumns: cols3, gap: 16 }}>
            <ReadOnlyField label="Name" value={vesselData.name} />
            <ReadOnlyField label="MMSI" value={vesselData.mmsi} />
            <ReadOnlyField label="IMO" value={vesselData.imo} />
            <ReadOnlyField label="Callsign" value={vesselData.callsign} />
            <ReadOnlyField label="AIS Type" value={vesselData.type_name} />
            <ReadOnlyField label="Length (m)" value={vesselData.length} />
            <ReadOnlyField label="Width (m)" value={vesselData.width} />
            <ReadOnlyField label="Draft (m)" value={vesselData.draft} />
          </div>
        </div>

        {/* ── Read-only: Equasis data ── */}
        {hasEquasis && (
          <div style={{ background: "white", borderRadius: 12, border: "1px solid #d0dcea", padding: "24px", display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ ...sectionHeader}}>
              Additional data
            </div>
            <div style={{ display: "grid", gridTemplateColumns: cols3, gap: 16 }}>
              <ReadOnlyField label="Flag" value={vesselData.equasis_flag} />
              <ReadOnlyField label="Built" value={vesselData.equasis_year_built} />
              <ReadOnlyField label="GT" value={vesselData.equasis_gt?.toLocaleString()} />
              <ReadOnlyField label="DWT" value={vesselData.equasis_dwt?.toLocaleString()} />
              <ReadOnlyField label="Owner" value={vesselData.equasis_owner} />
              {vesselData.equasis_manager && vesselData.equasis_manager !== "UNKNOWN" && (
                <ReadOnlyField label="Manager" value={vesselData.equasis_manager} />
              )}
            </div>
          </div>
        )}

        {/* ── Read-only: Managing Company ── */}
        {(companyName || vesselData.company_id) && (
          <div style={{ background: "white", borderRadius: 12, border: "1px solid #d0dcea", padding: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={sectionHeader}>
                Managing Company
              </div>
              {vesselData.company_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/companies/${vesselData.company_id}/edit`)}
                  style={{ fontSize: 12, color: "#63b3ed", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  Edit company →
                </button>
              )}
            </div>
            {companyName && (
              <div style={{ fontSize: 14, fontWeight: 500, color: "#1e3a5f", background: "#f8fafc", border: "1px solid #d0dcea", borderRadius: 6, padding: "8px 12px" }}>
                {companyName}
              </div>
            )}
            {!vesselData.company_id && (
              <div style={{ fontSize: 12, color: "#94afc9", marginTop: companyName ? 8 : 0 }}>
                No company linked yet.
              </div>
            )}
          </div>
        )}

        {/* ── Editable fields ── */}
        <form onSubmit={onSave} style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Certification */}
          <div style={{ background: "white", borderRadius: 12, border: "1px solid #d0dcea", padding: "24px", display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={sectionHeader}>
              Certification
            </div>
            <div>
              <label style={labelStyle}>
                Certification
              </label>
              <input
                name="certification"
                value={form.certification}
                onChange={onChange}
                placeholder="e.g. SOLAS, MLC 2006…"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Additional roles */}
          <div style={{ background: "white", borderRadius: 12, border: "1px solid #d0dcea", padding: "24px", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={sectionHeader}>
                Additional roles
              </div>
              <button
                type="button"
                onClick={() => setShowExtras(v => !v)}
                style={{ fontSize: 12, color: "#63b3ed", background: "none", border: "none", cursor: "pointer", padding: 0 }}
              >
                {showExtras ? "Hide" : "Select roles"}
              </button>
            </div>
            <div style={{ fontSize: 12, color: "#6b8cae" }}>
              {form.extra_type_ids?.length
                ? `Selected: ${types
                    .filter(t => form.extra_type_ids.includes(Number(t.id)))
                    .map(t => t.name)
                    .join(", ")}`
                : "None selected"}
            </div>
            {showExtras && (
              <div style={{ border: "1px solid #d0dcea", borderRadius: 8, padding: 12, background: "#f8fafc", display: "flex", flexDirection: "column", gap: 8 }}>
                <button
                  type="button"
                  style={{ fontSize: 12, padding: "4px 8px", border: "1px solid #d0dcea", borderRadius: 4, background: "white", cursor: "pointer", alignSelf: "flex-start" }}
                  onClick={() => setForm(p => ({ ...p, extra_type_ids: [] }))}
                >
                  Clear all
                </button>
                <div style={{ display: "grid", gridTemplateColumns: cols3, gap: 8 }}>
                  {types.map(t => {
                    const tid = Number(t.id);
                    const checked = (form.extra_type_ids || []).includes(tid);
                    return (
                      <label key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onChangeExtras(tid)}
                        />
                        {t.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Equipment fields — only for working vessels */}
          {showEquipment && (
            <div style={{ background: "white", borderRadius: 12, border: "1px solid #d0dcea", padding: "24px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={sectionHeader}>
                Equipment
              </div>
              <div style={{ display: "grid", gridTemplateColumns: cols3, gap: 16 }}>
                <YesNoSelect name="mbes" label="MBES" />
                <YesNoSelect name="sss" label="SSS" />
                <YesNoSelect name="mag" label="MAG" />
                <YesNoSelect name="a_frame" label="A-Frame" />
                <YesNoSelect name="helideck" label="Helideck" />
                <YesNoSelect name="moonpool" label="Moonpool" />
                <YesNoSelect name="fifi_pump" label="FiFi Pump" />
                <YesNoSelect name="accommodation_guests" label="Accommodation Guests" />
              </div>
            </div>
          )}

          <button
            type="submit"
            style={{
              width: "100%", background: "#1e3a5f", color: "white",
              padding: "10px 16px", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer",
            }}
          >
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
}
